"use client";

import * as React from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";

/** Derive up-to-2-char initials from a name or email. */
export function deriveInitials(name?: string | null, email?: string | null): string {
  const trimmed = name?.trim();
  if (trimmed) {
    const parts = trimmed.split(/\s+/);
    if (parts.length >= 2) return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    return trimmed.slice(0, 2).toUpperCase();
  }
  if (email) return email.slice(0, 2).toUpperCase();
  return "U";
}

interface UserAvatarProps {
  /** Public URL of the profile picture; falls back to initials when absent. */
  avatarUrl?: string | null;
  /** Display name used for initials + the image alt text. */
  name?: string | null;
  email?: string | null;
  /** Explicit initials override (when the caller already computed them). */
  initials?: string;
  /** Radix Avatar size token. Use `className` (e.g. `size-16`) for custom sizes. */
  size?: "sm" | "default" | "lg";
  className?: string;
  /** Tailwind classes for the initials fallback (gradient is the default). */
  fallbackClassName?: string;
}

/**
 * The user's profile picture: shows the uploaded image when present, otherwise
 * branded initials. One component for the header trigger, the dropdown header,
 * and the /account Profile tab so they always look consistent.
 */
export function UserAvatar({
  avatarUrl,
  name,
  email,
  initials,
  size = "default",
  className,
  fallbackClassName,
}: UserAvatarProps) {
  const resolvedInitials = initials ?? deriveInitials(name, email);
  const altName = name?.trim() || email || "Profile picture";
  return (
    <Avatar size={size} className={className}>
      {avatarUrl ? <AvatarImage src={avatarUrl} alt={altName} className="object-cover" /> : null}
      <AvatarFallback
        className={cn(
          "bg-gradient-to-br from-blue-500 to-blue-600 font-semibold text-white",
          fallbackClassName,
        )}
      >
        {resolvedInitials}
      </AvatarFallback>
    </Avatar>
  );
}
